import React, { useState, useEffect, useMemo } from 'react'
import { Navigation, FreeMode, Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import SearchProductCard from '../Search/Components/SearchProductCard';
import mapProduct from '../../../context/search/services/Voyado/helpers/mappers/mapProduct';
import { ISearchAndFilterProduct } from '../../../context/search/types';
import { elevate, localStorageBackedSession } from '@apptus/esales-api';
import { getClusterId, getMarket, getLocale, getTouchpoint, getViewId, getVoyadoSession, hasCookieConsent } from '../../../context/search/services/Voyado/helpers/voyadoHelpers';
import { elevateFetchShim } from '../../../context/search/services/Voyado/helpers/elevateFetchShim';
import fetchContextualPrices from '../../../context/search/services/Voyado/helpers/fetchContextualPrices';
import { useCart } from '../../../hooks/useCart';
import { useVoyadoRecommendationsCollector } from '../../../hooks/useVoyadoRecommendationsCollector';

interface VoyadoRecommendationsProps {
  dataSet: {
    productKey?: string;
    context?: string;
    pageReference?: string;
    presetRecommendationList?: string;
    recommendationList?: string;
    recommendationType?: string;
  }
}

const ProductRecommendations: React.FC<VoyadoRecommendationsProps> = ({
  dataSet: { productKey, context, pageReference, presetRecommendationList, recommendationList, recommendationType }
}): JSX.Element => {
  const [products, setProducts] = useState<ISearchAndFilterProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { items: cartItems } = useCart();

  // Generate stable sectionId from recommendationList or presetRecommendationList
  const sectionId = useMemo(() => {
    if (recommendationList) {
      try {
        const parsed = JSON.parse(recommendationList);
        const firstId = parsed.recommendationLists?.[0]?.id || parsed.recommendationLists?.[0]?.algorithm;
        return `section-${productKey || 'unknown'}-${firstId || 'default'}`;
      } catch {
        return `section-${productKey || 'unknown'}-${presetRecommendationList || 'default'}`;
      }
    }
    return `section-${productKey || 'unknown'}-${presetRecommendationList || 'default'}`;
  }, [productKey, recommendationList, presetRecommendationList]);

  const useCollector = productKey && !context;
  const collectorResult = useVoyadoRecommendationsCollector({
    productKey: useCollector ? productKey : '',
    context: useCollector ? context : '',
    pageReference: useCollector ? pageReference : '',
    presetRecommendationList: useCollector ? presetRecommendationList : '',
    recommendationList: useCollector ? recommendationList : '',
    recommendationType: useCollector ? recommendationType : '',
    sectionId
  });


  const api = useMemo(() => {
    // Use ephemeral session (random per request) if cookies declined, otherwise persisted session
    const session = getVoyadoSession(localStorageBackedSession);
    return elevate({
      clusterId: getClusterId(),
      market: getMarket(),
      locale: getLocale(),
      touchpoint: getTouchpoint(),
      session: session
    });
  }, [hasCookieConsent()]);

  // Use collector results if available
  useEffect(() => {
    if (useCollector && collectorResult) {
      setProducts(collectorResult.products || []);
      setIsLoading(collectorResult.isLoading);
      setError(collectorResult.error);
    }
  }, [useCollector, collectorResult]);

  useEffect(() => {
    // Skip if using collector
    if (useCollector) {
      return;
    }

    const fetchVoyadoData = async () => {
      try {
        elevateFetchShim();

        setIsLoading(true);
        setError(null);

        const hasConsent = hasCookieConsent();

        const isCartPage = window.location.pathname === '/cart';
        const useCartPageAPI = context === 'minicart-adjacent';

        if (!useCartPageAPI && !isCartPage && !productKey && !pageReference) {
          throw new Error('Either productKey or pageReference must be provided');
        }

        // Depending on how we want to get the data we have two data attributes, data-preset-recommendation-list and data-recommendation-list
        // data-preset-recommendation-list assumes the client has setup recs within the Voyado admin, and we simply get those products
        // data-recommendation-list allows the client to generate recs on the fly
        // If we're not using preset then we parse the data from the data attribute
        // If we're using preset we just send the primaryList object

        const parsedRecList = recommendationList ? JSON.parse(recommendationList) : null;
        if (parsedRecList) {
          console.info('[VoyadoRecs] parsed recommendationList', parsedRecList);
        } else if (presetRecommendationList) {
          console.info('[VoyadoRecs] using presetRecommendationList', presetRecommendationList);
        } else {
          console.info('[VoyadoRecs] no rec list provided; skipping API call');
          // No recommendations configured, don't make unnecessary API call
          setProducts([]);
          return;
        }

        let res;
        // Cart-based recommendations when rendered next to the minicart (use cartPage API)
        if (useCartPageAPI) {
          const productKeys = Array.isArray(cartItems)
            ? cartItems.map((ci: any) => ci && (ci.product_id || ci.id)).filter(Boolean).map((pk: number | string) => String(pk))
            : [];

          if (!productKeys.length) {
            // No valid product ids in cart; do not call cartPage with empty cart
            console.info('[VoyadoRecs] cart-based: no product keys in cart; skipping cartPage');
            setProducts([]);
            return;
          }

          const recommendationLists = parsedRecList
            ? parsedRecList
            : presetRecommendationList
              ? { primaryList: { include: false } }
              : {};

          const recLists = (recommendationLists && (recommendationLists as any).recommendationLists)
            ? (recommendationLists as any).recommendationLists.map((entry: any) => ({
              id: 'cart-recs',
              algorithm: 'CART',
              limit: entry.limit ?? 4,
            }))
            : [{ algorithm: 'CART', limit: 4 }];


          const cartParams: any = {
            viewId: getViewId(),
            cart: productKeys.join('|')
          };
          // Only include notify when cookies are declined (notify: false)
          if (!hasConsent) {
            cartParams.notify = false;
          }
          res = await api.query.cartPage(
            cartParams as any,
            {
              cart: { include: true },
              recommendationLists: recLists
            } as any
          );

        } else if (productKey) {
          // Product page: use productGroup, not primaryList
          const recommendationLists = parsedRecList
            ? parsedRecList
            : presetRecommendationList
              ? { productGroup: { include: false } }
              : {};

          const productParams: any = {
            viewId: getViewId(),
            productKey
          };
          // Only include notify when cookies are declined (notify: false)
          if (!hasConsent) {
            productParams.notify = false;
          }
          res = await api.query.productPage(
            productParams,
            recommendationLists
          );
        } else {
          // Landing page: only send body if we have custom recommendation lists
          // Preset recommendations don't need a body (GET request)
          // On cart page, use '/cart' as pageReference if not provided
          const recommendationLists = parsedRecList || null;

          let pageRef: string;
          if (isCartPage && !pageReference) {
            // Use '/cart' pageReference for cart page when not explicitly provided
            pageRef = '/cart';
          } else {
            pageRef = typeof pageReference === 'string'
              ? (pageReference.startsWith('/') ? pageReference : `/${pageReference}`)
              : '/';
          }

          const landingParams: any = {
            viewId: getViewId(),
            pageReference: pageRef
          };
          // Only include notify when cookies are declined (notify: false)
          if (!hasConsent) {
            landingParams.notify = false;
          }
          // Omit body for preset recs (GET) or custom recs (POST with body)
          res = recommendationLists
            ? await api.query.landingPage(landingParams, recommendationLists)
            : await api.query.landingPage(landingParams);
        }

        // If we have a preset we simply need to filter the results and get the relevant list
        // If we're generating a rec we need to grab all the product groups

        const returnedProductGroups = presetRecommendationList
          ? res.recommendationLists.find(list => list.id === presetRecommendationList)?.productGroups
          : res.recommendationLists.flatMap(recs => recs.productGroups);

        if (!returnedProductGroups?.length) {
          setProducts([]);
          return;
        }

        // Fetch contextual prices if not on GB site
        const productKeys = returnedProductGroups.map(group => group.products[0].key);
        let contextualPrices = {};

        const sfApiConfigStr = document.querySelector('[data-storefront-api-config]')?.textContent;
        let shouldFetchContextual = false;

        if (sfApiConfigStr) {
          try {
            const parsedConfig = JSON.parse(sfApiConfigStr);
            if (parsedConfig.country && parsedConfig.country !== 'GB') {
              shouldFetchContextual = true;
            }
          } catch (e) {
            // failed to parse config
          }
        }

        if (shouldFetchContextual && productKeys.length > 0) {
          contextualPrices = await fetchContextualPrices(productKeys);
        }

        const voyadoProductData = returnedProductGroups.flatMap((group, index: number) =>
          mapProduct(
            group.products[0],
            {
              index,
              pageIndex: 0, // Default for recommendations
              pageMeta: 'recommendations',
              recommendationType,
              contextualPriceData: contextualPrices[group.products[0].key]
            }
          )
        );


        setProducts(voyadoProductData);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An error occurred while fetching recommendations';
        console.error('[VoyadoRecs] error', err);
        setError(message);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVoyadoData();
    // Re-run when cart changes for minicart context
  }, [api, context, useCollector, ...(context === 'minicart-adjacent' ? [Array.isArray(cartItems) ? cartItems.length : 0] : []), recommendationList, productKey, pageReference]);

  if (isLoading) {
    return <div className="voyado-recommendations loading">Loading recommendations...</div>;
  }

  if (error) {
    return <div className="voyado-recommendations error">{error}</div>;
  }

  if (!products.length) {
    return null;
  }

  return (
    <section className="voyado-recommendations" data-voyado-recommendations data-context={context} data-recommendation-type={recommendationType}
    >
      <div className={`container ${context === '/' ? 'home' : ''}`}>
        {context === 'minicart-adjacent' && (
          <h5 className="minicart-recs__heading">You may also like</h5>
        )}
        <Swiper
          className="swiper"
          modules={[Navigation, FreeMode, Mousewheel]}
          {
            ...(context === 'minicart-adjacent'
              ? {
                // Vertical scrolling within the minicart sidebar
                direction: 'vertical' as const,
                freeMode: true,
                slidesPerView: 'auto' as const,
                spaceBetween: 16,
                loop: false,
                // Enable mouse wheel scrolling on desktop
                mousewheel: {
                  enabled: true,
                  forceToAxis: true,
                  sensitivity: 1,
                  releaseOnEdges: false,
                },
                // Reserve space for heading and paddings (32px top, 24px sides, 24px heading margin)
                style: { height: 'calc(100vh - 32px - 24px - 24px)' }
              }
              : {
                // Default horizontal configuration for other contexts
                loop: true,
                slidesPerView: 1.5,
                spaceBetween: 16,
                navigation: {
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev'
                },
                breakpoints: {
                  768: { slidesPerView: 3, spaceBetween: 16, loop: products.length > 3 },
                  1024: { slidesPerView: 4, spaceBetween: 8, freeMode: false, loop: products.length > 4 }
                }
              })
          }
        >
          {products.map((product) => (
            <SwiperSlide key={`recommendations-${product.ticket}`}>
              <SearchProductCard product={product} simple={true} />
            </SwiperSlide>
          ))}
          <button aria-label="Previous Slide" className="swiper-button swiper-button-prev" />
          <button aria-label="Next Slide" className="swiper-button swiper-button-next" />
        </Swiper>
      </div>
    </section>
  );
};

export default ProductRecommendations;